import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { conversationService } from '../services/conversationService'
import clientTokenService from '../services/clientTokenService'
import { ROUTES } from '../constants/routes'

/**
 * Page component for resuming a conversation.
 * Fetches conversation messages and loads them into the chat interface.
 */
export const ConversationResume = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const resumeConversation = async () => {
      try {
        // Validate conversation ID
        if (!id || id.length !== 36) {
          console.error('Invalid conversation ID')
          navigate(ROUTES.HOME)
          return
        }

        // Step 1: Set resume lock FIRST to prevent race conditions
        localStorage.setItem('et_ai_resume_lock', 'true')
        localStorage.setItem('et_ai_resume_conversation_id', id)

        // Fetch conversation messages
        const result = await conversationService.fetchConversationMessages(id)

        if (result.error) {
          console.error('Error fetching conversation:', result.error)
          // Clean up resume lock on error
          localStorage.removeItem('et_ai_resume_lock')
          localStorage.removeItem('et_ai_resume_conversation_id')
          navigate(ROUTES.HOME)
          return
        }

        if (result.messages && result.messages.length > 0) {
          // Format messages for localStorage storage
          const formattedMessages = conversationService.formatMessagesForChat(result.messages)

          // Extract start time from first message
          const startTime = result.messages[0]?.created_at
            ? new Date(result.messages[0].created_at).getTime()
            : Date.now()

          // Clean up any stale temporary keys from previous attempts before setting new ones
          localStorage.removeItem('resumedConversation')
          localStorage.removeItem('resumedConversationId')

          // Step 2: Set the conversation ID and start time (database values)
          localStorage.setItem('et_ai_conversation_id', id)
          localStorage.setItem('et_ai_conversation_start_time', startTime.toString())

          // Step 3: Update client token service with database values
          clientTokenService.switchToConversation(id, formattedMessages)

          // Save to localStorage (for backward compatibility)
          conversationService.saveConversationToLocal(id, result.messages)

          // Step 4: Prepare conversation history with database UUIDs
          const conversationHistory = formattedMessages.map((msg) => ({
            role: msg.role,
            content: msg.content,
            type: msg.type,
            isError: msg.isError,
            isProgress: msg.isProgress,
            trace: msg.trace, // Will be filtered by ErrorMessage component based on user level
            messageId: msg.messageId,
            timestamp: msg.timestamp,
            conversation_uuid: id, // Use database ID, not generated
            message_uuid: msg.message_uuid || msg.messageId,
          }))

          // Step 5: Store for ChatInterface
          localStorage.setItem('conversationHistory', JSON.stringify(conversationHistory))
          localStorage.setItem('isResumingConversation', 'true')

          // Step 6: Dispatch custom event to notify AuthContext
          const resumeData = {
            data: {
              id,
              messages: formattedMessages,
              startTime,
            },
            timestamp: Date.now(),
            operation: 'resume_conversation',
            source: 'sidebar',
            userLevel: null, // Not applicable for sidebar resume
            migrationId: null, // Not applicable for sidebar resume
          }

          // Dispatch custom event for AuthContext to listen to
          const event = new CustomEvent('conversation-resumed', {
            detail: resumeData,
          })
          window.dispatchEvent(event)

          // Step 7: Navigate to home (no location state needed anymore)
          navigate(ROUTES.HOME, { replace: true })
        } else {
          // No messages in conversation, clean up and go home
          console.warn('No messages found in conversation')
          localStorage.removeItem('et_ai_resume_lock')
          localStorage.removeItem('et_ai_resume_conversation_id')
          navigate(ROUTES.HOME)
        }
      } catch (error) {
        console.error('Error resuming conversation:', error)

        // Clean up ALL temporary keys on error
        localStorage.removeItem('et_ai_resume_lock')
        localStorage.removeItem('et_ai_resume_conversation_id')
        localStorage.removeItem('isResumingConversation')
        localStorage.removeItem('conversationHistory')

        navigate(ROUTES.HOME)
      }
    }

    resumeConversation()
  }, [id, navigate])

  // Show loading state while processing
  return (
    <div className="min-h-screen bg-dark-primary flex items-center justify-center">
      <div className="text-center">
        <div className="animate-pulse mb-4">
          <svg
            className="w-12 h-12 mx-auto text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-gray-400">Loading conversation...</p>
      </div>
    </div>
  )
}
